import {
  Alert,
  Badge,
  Button,
  Field,
  Modal,
  PageHeader,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  useToast,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Pencil, Plus } from 'lucide-react';
import { type JSX, useState } from 'react';

import { ApiError } from '../../api/client';
import { hasPermission } from '../../auth/has-permission';
import { useCurrentUser } from '../../auth/use-current-user';
import {
  createPaymentMethod,
  listPaymentMethods,
  type PaymentMethod,
  updatePaymentMethod,
} from '../../payments/payment-methods-api';

type ModalState = { mode: 'create' } | { mode: 'edit'; method: PaymentMethod } | null;

/** Codes from `PaymentExceptionFilter` (`apps/api` `payment-receipts.errors.filter.ts`). */
function mapPaymentMethodError(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'Ocurrió un error de conexión. Intentá de nuevo.';
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 409 && code === 'PAYMENT_METHOD_NAME_DUPLICATE') {
    return 'Ya existe un medio de pago con ese nombre.';
  }

  if (error.status === 403) {
    return 'No tenés permiso para realizar esta acción.';
  }

  if (error.status === 404) {
    return 'El medio de pago ya no existe. Actualizá la página.';
  }

  if (error.status === 422) {
    return 'Revisá los datos ingresados e intentá de nuevo.';
  }

  return 'Ocurrió un error inesperado. Intentá de nuevo.';
}

/**
 * `payment-methods/spec.md` — "Pantalla de configuración de medios de pago".
 * The hub card is gated on `settings.manage`; the page double-checks it
 * because a direct URL must not render the table.
 */
export function PaymentMethodConfigPage(): JSX.Element {
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [modal, setModal] = useState<ModalState>(null);
  const [name, setName] = useState('');

  const canManage = hasPermission(currentUser.data, 'settings.manage');
  const query = useQuery({
    queryKey: ['payment-methods'],
    queryFn: listPaymentMethods,
    enabled: canManage,
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (modal === null) {
        return;
      }

      const input = { name: name.trim() };

      if (modal.mode === 'create') {
        await createPaymentMethod(input);
      } else {
        await updatePaymentMethod(modal.method.id, input);
      }
    },
    onSuccess: async () => {
      toast('success', 'Guardado');
      setModal(null);
      await queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      updatePaymentMethod(id, { isActive }),
    onSuccess: async () => {
      toast('success', 'Guardado');
      await queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
    },
  });

  function openModal(state: ModalState): void {
    saveMutation.reset();
    setName(state?.mode === 'edit' ? state.method.name : '');
    setModal(state);
  }

  if (currentUser.isPending) {
    return <Skeleton className="h-40 w-full" />;
  }

  if (!canManage) {
    return (
      <Alert tone="danger" title="Sección restringida">
        No tenés permiso para acceder a los medios de pago.
      </Alert>
    );
  }

  const methods = query.data ?? [];

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={CreditCard}
        title="Medios de pago"
        description="Definí los medios con los que se registran las cobranzas."
        actions={
          <Button icon={Plus} onClick={() => openModal({ mode: 'create' })}>
            Nuevo medio de pago
          </Button>
        }
      />

      {query.isError ? (
        <Alert tone="danger" title="No pudimos cargar los medios de pago">
          <div className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al buscar. Intentá de nuevo.</span>
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {toggleActiveMutation.isError ? (
        <Alert tone="danger">{mapPaymentMethodError(toggleActiveMutation.error)}</Alert>
      ) : null}

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : null}

      {query.data !== undefined ? (
        <Table>
          <caption className="sr-only">Medios de pago</caption>
          <thead>
            <tr>
              <TableHeader>Nombre</TableHeader>
              <TableHeader>Recibos que lo usan</TableHeader>
              <TableHeader>Estado</TableHeader>
              <TableHeader>Acciones</TableHeader>
            </tr>
          </thead>
          <TableBody>
            {methods.map((method) => (
              <TableRow key={method.id}>
                <TableCell>{method.name}</TableCell>
                <TableCell>{method.paymentUsageCount}</TableCell>
                <TableCell>
                  <Badge variant={method.isActive ? 'success' : 'neutral'}>
                    {method.isActive ? 'Activo' : 'Inactivo'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={Pencil}
                      aria-label={`Editar ${method.name}`}
                      onClick={() => openModal({ mode: 'edit', method })}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={
                        toggleActiveMutation.isPending &&
                        toggleActiveMutation.variables?.id === method.id
                      }
                      onClick={() =>
                        toggleActiveMutation.mutate({ id: method.id, isActive: !method.isActive })
                      }
                    >
                      {method.isActive ? 'Desactivar' : 'Activar'}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}

      {modal !== null ? (
        <Modal
          open
          onClose={() => setModal(null)}
          title={modal.mode === 'create' ? 'Nuevo medio de pago' : 'Editar medio de pago'}
          className="max-w-md"
        >
          <div className="flex flex-col gap-4">
            {saveMutation.isError ? (
              <Alert tone="danger">{mapPaymentMethodError(saveMutation.error)}</Alert>
            ) : null}

            <Field
              label="Nombre"
              name="name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />

            <div className="flex items-center gap-3">
              <Button
                loading={saveMutation.isPending}
                disabled={name.trim() === ''}
                onClick={() => saveMutation.mutate()}
              >
                Confirmar
              </Button>
              <Button variant="secondary" onClick={() => setModal(null)}>
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
